/**
 * Service Diagrams Route (issue #1077, epic #1002 sub-issue 6/7)
 *
 * Read-only entry point for a service's generated/authored diagrams —
 * surfaced the same way as the existing Dependencies/Impact tabs
 * (`$serviceId.dependencies.tsx` / `$serviceId.impact.tsx`). The viewer
 * itself stays read-only by design; this route additionally owns the
 * "Generate Diagram" trigger for `POST /diagrams/generate` (issue #1649) —
 * the diagram content is fully backend-built (epic #1002), but until now
 * there was no self-service entry point to produce it.
 */

import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useService, useDiagramsForEntity } from '../../graphql/queries';
import DiagramViewer from '../../components/diagrams/DiagramViewer';
import { LoadingSpinner } from '../../components/shared/LoadingSpinner';
import { EmptyState } from '../../components/shared/EmptyState';
import { DeleteConfirmationDialog } from '../../components/shared/DeleteConfirmationDialog';
import { generateDiagram, ApiError } from '../../lib/apiClient';
import { usePermission } from '../../hooks/usePermission';
import './$serviceId.diagrams.css';

const ForgeButton = 'forge-button' as React.ElementType;
const BannerElement = 'forge-banner' as React.ElementType;

export const Route = createFileRoute('/services/$serviceId/diagrams')({
  component: ServiceDiagramsPage,
});

function ServiceDiagramsPage(): JSX.Element {
  const { serviceId } = Route.useParams();
  const canGenerate = usePermission('diagram:create');

  const { data: serviceData, loading: serviceLoading, error: serviceError } = useService(serviceId);

  const {
    data: diagramsData,
    loading: diagramsLoading,
    error: diagramsError,
    refetch: refetchDiagrams,
  } = useDiagramsForEntity(serviceId);

  // "Generate Diagram" trigger state (issue #1649). A 409 from the backend
  // (a generated diagram already exists) opens a confirm-overwrite dialog
  // instead of failing silently; confirming re-calls with
  // `overwriteExisting: true`.
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);

  const runGenerate = (overwriteExisting: boolean): void => {
    setIsGenerating(true);
    setGenerateError(null);
    generateDiagram({
      entityType: 'Service',
      entityId: serviceId,
      type: 'c4-system-context',
      overwriteExisting,
    })
      .then(() => {
        setConfirmOverwriteOpen(false);
        return refetchDiagrams();
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 409) {
          setConfirmOverwriteOpen(true);
          return;
        }
        setGenerateError(err instanceof ApiError ? err.message : 'Failed to generate diagram');
      })
      .finally(() => setIsGenerating(false));
  };

  const handleGenerateClick = (): void => {
    setGenerateError(null);
    runGenerate(false);
  };

  const handleConfirmOverwrite = (): void => {
    runGenerate(true);
  };

  const handleCancelOverwrite = (): void => {
    setConfirmOverwriteOpen(false);
  };

  const loading = serviceLoading || diagramsLoading;

  // Loading state
  if (loading && !serviceData) {
    return (
      <div className="service-diagrams-page">
        <LoadingSpinner message="Loading service diagrams..." />
      </div>
    );
  }

  // Error state
  if (serviceError) {
    return (
      <div className="service-diagrams-page">
        <EmptyState
          title="Failed to load service"
          description={serviceError.message || 'An error occurred while loading the service'}
          icon="error"
        />
      </div>
    );
  }

  // Service not found
  if (!serviceData?.service) {
    return (
      <div className="service-diagrams-page">
        <EmptyState
          title="Service not found"
          description={`Service with ID ${serviceId} does not exist`}
          icon="error"
        />
      </div>
    );
  }

  const service = serviceData.service;
  const diagrams = diagramsData?.diagramsForEntity ?? [];
  const diagramsErrorMessage = diagramsError ? diagramsError.message : null;

  return (
    <div className="service-diagrams-page">
      {/* Header */}
      <div className="diagrams-header">
        <div className="diagrams-breadcrumb">
          <Link
            to="/services/$serviceId"
            params={{ serviceId }}
            className="diagrams-breadcrumb-link"
          >
            ← Back to Service
          </Link>
        </div>
        <div className="diagrams-title-section">
          <div className="diagrams-title-row">
            <h1>{service.name} - Diagrams</h1>
            {canGenerate && (
              <ForgeButton
                type="button"
                variant="raised"
                disabled={isGenerating}
                onClick={handleGenerateClick}
                data-testid="generate-diagram-btn"
              >
                <forge-icon name="autorenew" slot="start" aria-hidden="true"></forge-icon>
                {isGenerating ? 'Generating…' : 'Generate Diagram'}
              </ForgeButton>
            )}
          </div>
          <p className="diagrams-subtitle">
            Read-only view of this service&apos;s System Context diagram(s)
          </p>
        </div>
      </div>

      {generateError && (
        <BannerElement
          theme="error"
          dismissed={false}
          role="alert"
          className="diagrams-generate-banner"
          data-testid="generate-diagram-error"
        >
          <span>{generateError}</span>
        </BannerElement>
      )}

      {/* Diagram Viewer */}
      <div className="diagrams-viewer-wrapper">
        <DiagramViewer diagrams={diagrams} loading={diagramsLoading} error={diagramsErrorMessage} />
      </div>

      {/* Confirm-overwrite dialog (issue #1649). Mounted only while a 409
          conflict is pending so this page never has two forge-dialog
          elements in the DOM at once (a second always-mounted dialog
          breaks strict-mode `forge-dialog` locators — see
          ServiceProjectAssignmentsSection's Remove Assignment dialog for
          the same convention). */}
      {confirmOverwriteOpen && (
        <DeleteConfirmationDialog
          open
          title="Replace existing diagram?"
          message="A generated System Context diagram already exists for this service. Generating again will replace its content."
          warning="This overwrites the existing generated diagram; it cannot be undone."
          itemName={`${service.name} — System Context`}
          onConfirm={handleConfirmOverwrite}
          onCancel={handleCancelOverwrite}
          isDeleting={isGenerating}
          confirmLabel="Overwrite & Generate"
          confirmingLabel="Generating…"
        />
      )}
    </div>
  );
}

// Export for testing
export { ServiceDiagramsPage };
